// src/services/sisterGroups.ts
// ----------------------------
// Pure helpers for the db-205 "sister services" feature (no database access here):
//   * shortServiceCode   — "ZephyrWA006" -> "WA006" for compact chips.
//   * buildSisterIndex   — flat memberships + groups + shelters -> per-service
//                          { group, sisters } map (one pass; used by every screen).
//   * saveShelterSisterGroup — the editor's save steps, with the data calls injected
//                          so the ordering + stop-on-error rules are unit-testable.

export type SisterShelter = {
  id: string;
  shelter_code: string | null;
  shelter_name: string | null;
  deactivated?: boolean | null;
};
export type SisterMember = { id: string; sister_group_id: string; shelter_id: string };
export type SisterGroup = { id: string; name: string };
export type Sister = { id: string; code: string; name: string };
export type SisterEntry = {
  groupId: string;
  groupName: string;
  memberId: string;
  sisters: Sister[];
};

export function shortServiceCode(code: string | null | undefined): string {
  if (!code) return "";
  return code.startsWith("Zephyr") ? code.slice("Zephyr".length) : code;
}

// For every ACTIVE service that is in a group: its group + its sisters (other ACTIVE
// services in the same group, sorted by code, excluding itself). Services in no group
// (or whose group / shelter is unknown, or that are deactivated) have no entry.
export function buildSisterIndex(
  members: SisterMember[] | null | undefined,
  groups: SisterGroup[] | null | undefined,
  shelters: SisterShelter[] | null | undefined
): Map<string, SisterEntry> {
  const out = new Map<string, SisterEntry>();
  const groupName = new Map((groups ?? []).map((g) => [g.id, g.name] as const));
  const shelterById = new Map((shelters ?? []).map((s) => [s.id, s] as const));

  // Group -> its active, known members.
  const byGroup = new Map<string, { member: SisterMember; shelter: SisterShelter }[]>();
  for (const m of members ?? []) {
    const s = shelterById.get(m.shelter_id);
    if (!s || s.deactivated || !groupName.has(m.sister_group_id)) continue;
    const list = byGroup.get(m.sister_group_id) ?? [];
    list.push({ member: m, shelter: s });
    byGroup.set(m.sister_group_id, list);
  }

  for (const [gid, list] of byGroup) {
    const all: Sister[] = list
      .map(({ shelter }) => ({
        id: shelter.id,
        code: shortServiceCode(shelter.shelter_code),
        name: shelter.shelter_name ?? "",
      }))
      .sort((a, b) => a.code.localeCompare(b.code, undefined, { numeric: true }));
    for (const { member, shelter } of list) {
      out.set(shelter.id, {
        groupId: gid,
        groupName: groupName.get(gid) ?? "",
        memberId: member.id,
        sisters: all.filter((x) => x.id !== shelter.id),
      });
    }
  }
  return out;
}

// True only when the service has at least one ACTIVE sister (the DFV Services list
// "Only services in a sister group" filter). A missing index (not loaded / failed)
// is treated as "no sisters" — fail-safe.
export function hasSisters(
  index: Map<string, SisterEntry> | null | undefined,
  shelterId: string
): boolean {
  return (index?.get(shelterId)?.sisters.length ?? 0) > 0;
}

type WriteError = { message: string } | null;
export type SisterSaveDeps = {
  deactivateSisterMember: (memberId: string) => Promise<{ error: WriteError }>;
  createSisterGroup: (name: string) => Promise<{ id: string | null; error: WriteError }>;
  addSisterMember: (groupId: string, shelterId: string) => Promise<{ error: WriteError }>;
};
export type SisterChoice = { groupId?: string; newName?: string; none?: boolean };

// The editor's Save. Order matters: the DB allows ONE active membership per service,
// so the old row is deactivated BEFORE the new one is added. Stops at the first error.
// If a later step fails, the service is simply left in no group (never in two).
export async function saveShelterSisterGroup(
  args: {
    shelterId: string;
    currentMemberId: string | null;
    currentGroupId: string | null;
    choice: SisterChoice;
  },
  deps: SisterSaveDeps
): Promise<{ error: WriteError }> {
  const { shelterId, currentMemberId, currentGroupId, choice } = args;

  if (choice.newName !== undefined && !choice.newName.trim()) {
    return { error: { message: "A group name is required." } };
  }
  if (choice.groupId && choice.groupId === currentGroupId) return { error: null };
  if (choice.none && !currentMemberId) return { error: null };

  if (currentMemberId) {
    const { error } = await deps.deactivateSisterMember(currentMemberId);
    if (error) return { error };
  }
  if (choice.none) return { error: null };

  let targetGroupId = choice.groupId ?? null;
  if (!targetGroupId && choice.newName !== undefined) {
    const { id, error } = await deps.createSisterGroup(choice.newName);
    if (error) return { error };
    targetGroupId = id;
  }
  if (!targetGroupId) return { error: { message: "No group chosen." } };

  const { error } = await deps.addSisterMember(targetGroupId, shelterId);
  return { error };
}
